// 検索画面の状態オーケストレーション。
// 入力 → (300ms debounce) → (かな or kuromoji で)読み推定 → 母音正規化 → Worker 検索 → 結果、を束ねる。
// 読みは tokens が唯一の真実（チップ編集で上書き可）。debouncedInput の変化時だけ tokens を作り直す。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { analyzeReading, isAllKana, toKatakana, type ReadingToken } from '../nlp/tokenizer'
import { readingToKey, serialize, toMorae } from '../core/vowel'
import { type Flags } from '../core/toggles'
import {
  getSearchWorker,
  type RhymeMode,
  type RunResult,
  type WorkerResult,
  type ResultFilter,
  type SortOrder,
} from '../workers/searchClient'
import type { Meta } from '../core/loadIndex'

const BASE_URL = import.meta.env.BASE_URL
const MAX_DEPTH = 8

export type ReadingStatus = 'idle' | 'loading' | 'ready' | 'error'

/** 2語コンボの1組。 */
export interface ComboPair {
  leftSurface: string
  leftReading: string
  rightSurface: string
  rightReading: string
}

export interface SearchState {
  rawInput: string
  setRawInput: (v: string) => void
  tokens: ReadingToken[]
  setTokenReading: (index: number, reading: string) => void
  reading: string
  rhymeMode: RhymeMode
  setRhymeMode: (m: RhymeMode) => void
  depth: number
  setDepth: (d: number) => void
  maxDepth: number
  flags: Flags
  setFlags: (f: Flags) => void
  filter: ResultFilter
  setFilter: (f: ResultFilter) => void
  sort: SortOrder
  setSort: (s: SortOrder) => void
  slack: number
  setSlack: (v: number) => void
  results: WorkerResult[]
  total: number
  tookMs: number
  queryKey: string
  usedDepth: number | null
  combos: ComboPair[]
  workerReady: boolean
  meta: Meta | null
  readingStatus: ReadingStatus
  runExample: (text: string) => void
}

interface UrlState {
  q: string
  m: RhymeMode
  d: number
  flags: Flags
  filter: ResultFilter
  sort: SortOrder
  slack: number
}

function parseUrl(): UrlState {
  const p = new URLSearchParams(location.search)
  const modeParam = p.get('m')
  // 既定は「どこかで踏む」（フルマッチ優先）。tail/head/exact は明示指定時のみ。
  const m: RhymeMode =
    modeParam === 'tail' || modeParam === 'head' || modeParam === 'exact' ? modeParam : 'mid'
  const d = Number(p.get('d'))
  const f = p.get('f') ?? ''
  const sParam = p.get('s')
  const sort: SortOrder = sParam === 'mora' || sParam === 'kana' ? sParam : 'freq'
  const posParam = p.get('p')
  const pos = posParam
    ? posParam
        .split(',')
        .map(Number)
        .filter((v) => Number.isInteger(v) && v >= 0 && v < 32)
    : []
  const mn = Number(p.get('mn'))
  const mx = Number(p.get('mx'))
  const k = Number(p.get('k'))
  return {
    q: p.get('q') ?? '',
    m,
    // 既定の深さは常に最大（フルマッチ優先）。maxDepth 側で実際のキー長にクランプされる。
    d: Number.isFinite(d) && d > 0 ? Math.min(d, MAX_DEPTH) : MAX_DEPTH,
    flags: {
      choon: f.includes('C') ? 'ignore' : 'inherit',
      sokuon: f.includes('S') ? 'boundary' : 'skip',
      hatsuon: f.includes('H') ? 'include' : 'exclude',
      longVowelNorm: f.includes('L'),
    },
    filter: {
      pos: pos.length > 0 ? pos : undefined,
      moraMin: Number.isInteger(mn) && mn > 0 ? mn : undefined,
      moraMax: Number.isInteger(mx) && mx > 0 ? mx : undefined,
    },
    sort,
    slack: k === 1 || k === 2 ? k : 0,
  }
}

function flagsParam(f: Flags): string {
  return (
    (f.choon === 'ignore' ? 'C' : '') +
    (f.sokuon === 'boundary' ? 'S' : '') +
    (f.hatsuon === 'include' ? 'H' : '') +
    (f.longVowelNorm ? 'L' : '')
  )
}

export function useSearchState(): SearchState {
  const initial = useMemo(parseUrl, [])
  const [rawInput, setRawInput] = useState(initial.q)
  const [tokens, setTokens] = useState<ReadingToken[]>([])
  const [rhymeMode, setRhymeMode] = useState<RhymeMode>(initial.m)
  const [depth, setDepthRaw] = useState(initial.d)
  const [flags, setFlags] = useState<Flags>(initial.flags)
  const [filter, setFilter] = useState<ResultFilter>(initial.filter)
  const [sort, setSort] = useState<SortOrder>(initial.sort)
  const [slack, setSlackRaw] = useState(initial.slack)
  const setSlack = useCallback((v: number) => setSlackRaw(Math.max(0, Math.min(2, Math.round(v)))), [])

  const [results, setResults] = useState<WorkerResult[]>([])
  const [total, setTotal] = useState(0)
  const [tookMs, setTookMs] = useState(0)
  const [queryKey, setQueryKey] = useState('')
  const [usedDepth, setUsedDepth] = useState<number | null>(null)
  const [combos, setCombos] = useState<ComboPair[]>([])
  const [workerReady, setWorkerReady] = useState(false)
  const [meta, setMeta] = useState<Meta | null>(null)
  const [readingStatus, setReadingStatus] = useState<ReadingStatus>('idle')

  const reading = useMemo(() => tokens.map((t) => t.reading).join(''), [tokens])
  const keyLen = useMemo(() => readingToKey(reading, flags).length, [reading, flags])
  const maxDepth = Math.max(1, Math.min(keyLen || 1, MAX_DEPTH))
  const depthClamped = Math.min(depth, maxDepth)

  // 1) Worker 初期化（コーパス読み込み＋既定索引）。
  useEffect(() => {
    let alive = true
    getSearchWorker()
      .init(BASE_URL)
      .then((m) => {
        if (!alive) return
        setMeta(m)
        setWorkerReady(true)
      })
      .catch((e) => console.error('[inkyo] worker init 失敗', e))
    return () => {
      alive = false
    }
  }, [])

  // 2) rawInput → debouncedInput（入力中は検索を走らせない）。
  const [debouncedInput, setDebouncedInput] = useState(initial.q)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedInput(rawInput), 300)
    return () => clearTimeout(timer)
  }, [rawInput])

  // 3) debouncedInput → tokens（かなは即時、漢字/フレーズは kuromoji で読み推定）。
  const readingSeq = useRef(0)
  useEffect(() => {
    const text = debouncedInput.trim()
    if (!text) {
      setTokens([])
      setReadingStatus('idle')
      return
    }
    if (isAllKana(text)) {
      setTokens([{ surface: text, reading: toKatakana(text.replace(/\s+/g, '')) }])
      setReadingStatus('ready')
      return
    }
    const seq = ++readingSeq.current
    setReadingStatus('loading')
    analyzeReading(text)
      .then((toks) => {
        if (seq !== readingSeq.current) return
        setTokens(toks.filter((t) => t.reading.length > 0))
        setReadingStatus('ready')
      })
      .catch((e) => {
        if (seq !== readingSeq.current) return
        console.error('[inkyo] 読み推定失敗', e)
        setReadingStatus('error')
      })
  }, [debouncedInput])

  // 4) reading / mode / depth / flags → Worker 検索。
  const runSeq = useRef(0)
  useEffect(() => {
    if (!workerReady) return
    if (!reading) {
      setResults([])
      setTotal(0)
      setQueryKey('')
      setUsedDepth(null)
      setCombos([])
      return
    }
    const annotated = serialize(toMorae(reading))
    const excludeSurface = tokens.map((t) => t.surface).join('')
    const seq = ++runSeq.current
    getSearchWorker()
      .run({
        annotated,
        rhymeMode,
        depth: depthClamped,
        flags,
        limit: 300,
        filter,
        sort,
        slack,
        excludeSurface,
      })
      .then((res: RunResult) => {
        if (seq !== runSeq.current) return
        setResults(res.results)
        setTotal(res.total)
        setTookMs(res.tookMs)
        setQueryKey(res.queryKey)
        setUsedDepth(res.usedDepth ?? null)
      })
      .catch((e) => console.error('[inkyo] 検索失敗', e))

    // コンボ提案は tail/exact のみ（他モードは母音キーの2分割が意味をなさない）。並行に取得する。
    if (rhymeMode === 'tail' || rhymeMode === 'exact') {
      getSearchWorker()
        .combos({ annotated, flags, excludeSurface })
        .then((res) => {
          if (seq !== runSeq.current) return
          setCombos(res.pairs)
        })
        .catch((e) => {
          if (seq !== runSeq.current) return
          console.error('[inkyo] コンボ取得失敗', e)
          setCombos([])
        })
    } else {
      setCombos([])
    }
  }, [reading, rhymeMode, depthClamped, flags, filter, sort, slack, workerReady])

  // 5) URL 同期（共有・リロード復元）。
  useEffect(() => {
    const p = new URLSearchParams()
    if (rawInput.trim()) p.set('q', rawInput.trim())
    if (rhymeMode !== 'mid') p.set('m', rhymeMode)
    if (rhymeMode === 'tail' && depthClamped !== maxDepth) p.set('d', String(depthClamped))
    const f = flagsParam(flags)
    if (f) p.set('f', f)
    if (sort !== 'freq') p.set('s', sort)
    if (filter.pos?.length) p.set('p', filter.pos.join(','))
    if (filter.moraMin) p.set('mn', String(filter.moraMin))
    if (filter.moraMax) p.set('mx', String(filter.moraMax))
    if (rhymeMode === 'tail' && slack > 0) p.set('k', String(slack))
    const qs = p.toString()
    history.replaceState(null, '', qs ? `?${qs}` : location.pathname)
  }, [rawInput, rhymeMode, depthClamped, maxDepth, flags, filter, sort, slack])

  const setTokenReading = useCallback((index: number, r: string) => {
    setTokens((prev) =>
      prev.map((t, i) => (i === index ? { ...t, reading: toKatakana(r).replace(/[^ァ-ヴー]/g, '') } : t)),
    )
  }, [])

  const setDepth = useCallback((d: number) => setDepthRaw(Math.max(1, Math.min(d, MAX_DEPTH))), [])

  const runExample = useCallback((text: string) => setRawInput(text), [])

  return {
    rawInput,
    setRawInput,
    tokens,
    setTokenReading,
    reading,
    rhymeMode,
    setRhymeMode,
    depth: depthClamped,
    setDepth,
    maxDepth,
    flags,
    setFlags,
    filter,
    setFilter,
    sort,
    setSort,
    slack,
    setSlack,
    results,
    total,
    tookMs,
    queryKey,
    usedDepth,
    combos,
    workerReady,
    meta,
    readingStatus,
    runExample,
  }
}
